import React, { useMemo } from 'react';
import type { BlueprintFormField } from '../../../types/processBlueprint';
import type { ExecutionField } from '../../../types/card';

type FormField = BlueprintFormField | ExecutionField;

interface MinhaMesaFormFieldsProps {
  fields: FormField[];
  formState: Record<string, unknown>;
  onFieldChange: (fieldId: string, value: unknown) => void;
  readOnly?: boolean;
  invalidFieldIds?: string[];
}

export const MinhaMesaFormFields: React.FC<MinhaMesaFormFieldsProps> = ({
  fields,
  formState,
  onFieldChange,
  readOnly = false,
  invalidFieldIds = [],
}) => {
  if (fields.length === 0) {
    return null;
  }

  const firstFocusableFieldId = useMemo(() => {
    const firstRequiredEmpty = fields.find((field) => {
      if (!field.required || readOnly) return false;
      const value = formState[field.id] ?? field.defaultValue ?? '';
      return value === '' || value === undefined || value === null;
    });
    return firstRequiredEmpty?.id ?? null;
  }, [fields, formState, readOnly]);

  const baseInputClass =
    'w-full rounded-[4px] border border-slate-300 bg-slate-50 px-3 py-2 text-[13px] text-slate-800 transition hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';

  const groups = useMemo(() => {
    const map = new Map<string, FormField[]>();
    for (const field of fields) {
      const g = (field as any).groupName ?? '';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(field);
    }
    return Array.from(map.entries());
  }, [fields]);

  const renderField = (field: FormField) => {
    const value = formState[field.id] ?? field.defaultValue ?? '';
    const ph = (field as any).placeholder as string | undefined;
    const autoFocus = field.id === firstFocusableFieldId;

    return (
      <div key={field.id} data-field-id={field.id} className="scroll-mt-24 p-2">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-700 dark:text-slate-300">
          {field.label}
          {field.required && <span className="ml-0.5 text-rose-500">*</span>}
        </label>
        {(field as any).description && (
          <p className="mb-1 text-[10px] text-slate-500 dark:text-slate-400">{(field as any).description}</p>
        )}

        {field.type === 'text' && (
          <input
            type="text"
            value={String(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={readOnly}
            placeholder={ph ?? 'Preencha este campo'}
            autoFocus={autoFocus}
            className={baseInputClass}
          />
        )}

        {field.type === 'number' && (
          <input
            type="number"
            value={value === '' ? '' : Number(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value === '' ? '' : Number(e.target.value))}
            disabled={readOnly}
            placeholder={ph ?? 'Preencha este campo'}
            autoFocus={autoFocus}
            className={baseInputClass}
          />
        )}

        {field.type === 'currency' && (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
            <input
              type="number"
              step="0.01"
              value={value === '' ? '' : Number(value)}
              onChange={(e) => onFieldChange(field.id, e.target.value === '' ? '' : Number(e.target.value))}
              disabled={readOnly}
              placeholder={ph}
              autoFocus={autoFocus}
              className={`${baseInputClass} pl-9`}
            />
          </div>
        )}

        {field.type === 'date' && (
          <input
            type="date"
            value={String(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={readOnly}
            autoFocus={autoFocus}
            className={baseInputClass}
          />
        )}

        {field.type === 'textarea' && (
          <textarea
            value={String(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={readOnly}
            rows={3}
            placeholder={ph}
            autoFocus={autoFocus}
            className={baseInputClass}
          />
        )}

        {field.type === 'select' && (
          <select
            value={String(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={readOnly}
            autoFocus={autoFocus}
            className={baseInputClass}
          >
            <option value="">Selecione...</option>
            {(field.options ?? []).map((opt) => {
              const val = typeof opt === 'string' ? opt : opt.value;
              const lbl = typeof opt === 'string' ? opt : opt.label;
              return <option key={val} value={val}>{lbl}</option>;
            })}
          </select>
        )}

        {field.type === 'checkbox' && (
          <label className="flex items-center gap-2 rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onFieldChange(field.id, e.target.checked)}
              disabled={readOnly}
              autoFocus={autoFocus}
              className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600"
            />
            <span className="text-[12px] text-slate-600 dark:text-slate-400">Sim</span>
          </label>
        )}

        {field.type === 'file' && (
          <input
            type="file"
            disabled={readOnly}
            onChange={() => {/* TODO: implementar upload */}}
            className="text-sm text-slate-500 file:mr-4 file:rounded-[4px] file:border file:border-slate-200 file:bg-slate-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:file:border-neutral-700 dark:file:bg-blue-900/30 dark:file:text-blue-400"
          />
        )}
      </div>
    );
  };

  const hasGroups = groups.some(([g]) => g !== '');

  if (!hasGroups) {
    return <div className="space-y-3">{fields.map(renderField)}</div>;
  }

  return (
    <div className="space-y-4">
      {groups.map(([groupName, groupFields]) => (
        <div key={groupName || '__ungrouped'}>
          {groupName && !/^novo\s*grupo$/i.test(groupName.trim()) && (
            <div className="mb-2 border-b border-slate-200 pb-1 dark:border-slate-700">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                {groupName}
              </span>
            </div>
          )}
          <div className="space-y-3">
            {groupFields.map(renderField)}
          </div>
        </div>
      ))}
    </div>
  );
};
